'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronRight } from 'lucide-react';
import { cn } from './lib/utils';
import { Checkbox } from './checkbox';
import { Pagination } from './pagination';
import { Skeleton } from './skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';

export type SortDirection = 'asc' | 'desc' | null;

export interface ColumnDef<T> {
  key: keyof T | string;
  header: string;
  sortable?: boolean;
  className?: string;
  width?: string | number;
  render?: (value: unknown, row: T) => React.ReactNode;
}

interface SortState {
  key: string;
  direction: SortDirection;
}

/** Drives {@link Pagination} from caller-owned state instead of the
 *  table's internal page/pageSize — for server-paginated data, where `data`
 *  is already just the current page and slicing it again would be wrong. */
export interface ServerPaginationState {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
}

interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  selectable?: boolean;
  onSelectionChange?: (selectedRows: T[]) => void;
  keyExtractor: (row: T) => string | number;
  className?: string;
  emptyText?: string;
  loading?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  /** Minimum table width; when the container is narrower the table scrolls horizontally. */
  minWidth?: number | string;
  /** Set when the caller already paginates `data` (e.g. server-side paging) to hide the built-in pagination bar. */
  hidePagination?: boolean;
  /** When set, the pagination bar is driven by this instead of internal page/pageSize state — use for server-side paging. Implies `hidePagination` is ignored (server pagination bar always renders). */
  serverPagination?: ServerPaginationState;
  /** Renders an expandable detail panel below a row. When set, a leading toggle column is added to every row. */
  expandedRowRender?: (row: T) => React.ReactNode;
  /** Which row keys start expanded. */
  defaultExpandedKeys?: (string | number)[];
}

function sortTooltipLabel(direction: SortDirection, t: (key: string) => string): string {
  if (direction === 'asc') return t('table.sortDescending');
  if (direction === 'desc') return t('table.clearSort');
  return t('table.sortAscending');
}

function SortIcon({ direction }: { direction: SortDirection }) {
  if (direction === 'asc') return <ChevronUp className="size-4" />;
  if (direction === 'desc') return <ChevronDown className="size-4" />;
  return <ChevronsUpDown className="size-4 opacity-40" />;
}

function getValue<T>(row: T, key: string): unknown {
  return (row as Record<string, unknown>)[key];
}

function sortData<T>(data: T[], sort: SortState): T[] {
  if (!sort.direction) return data;
  return [...data].sort((a, b) => {
    const aVal = getValue(a, sort.key);
    const bVal = getValue(b, sort.key);
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    return sort.direction === 'asc' ? cmp : -cmp;
  });
}

export function DataTable<T>({
  data,
  columns,
  selectable = false,
  onSelectionChange,
  keyExtractor,
  className,
  emptyText,
  loading = false,
  pageSize: initialPageSize = 10,
  pageSizeOptions,
  minWidth,
  hidePagination = false,
  serverPagination,
  expandedRowRender,
  defaultExpandedKeys,
}: DataTableProps<T>) {
  const { t } = useTranslation('ui');
  const [sort, setSort] = React.useState<SortState>({ key: '', direction: null });
  const [selectedKeys, setSelectedKeys] = React.useState<Set<string | number>>(new Set());
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(initialPageSize);
  const [expandedKeys, setExpandedKeys] = React.useState<Set<string | number>>(
    () => new Set(defaultExpandedKeys),
  );

  const hasFixedWidths = columns.some((col) => col.width != null);

  const sortedData = React.useMemo(() => sortData(data, sort), [data, sort]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [data, sort.key, sort.direction, pageSize]);

  const pagedData = React.useMemo(() => {
    // Server-driven and caller-paginated (`hidePagination`) data is already
    // sliced to the current page — only the fully-local mode re-slices here.
    if (serverPagination || hidePagination) return sortedData;
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize, hidePagination, serverPagination]);

  function toggleExpanded(key: string | number) {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const allSelected =
    pagedData.length > 0 && pagedData.every((row) => selectedKeys.has(keyExtractor(row)));
  const someSelected = pagedData.some((row) => selectedKeys.has(keyExtractor(row))) && !allSelected;

  function handleSort(key: string) {
    setSort((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return { key: '', direction: null };
    });
  }

  function handleRowSelect(row: T, checked: boolean) {
    const key = keyExtractor(row);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  }

  function handleSelectAll(checked: boolean) {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      pagedData.forEach((row) => {
        if (checked) next.add(keyExtractor(row));
        else next.delete(keyExtractor(row));
      });
      return next;
    });
  }

  React.useEffect(() => {
    if (!selectable || !onSelectionChange) return;
    const selected = sortedData.filter((row) => selectedKeys.has(keyExtractor(row)));
    onSelectionChange(selected);
  }, [selectedKeys]);

  return (
    <TooltipProvider>
      <div className={cn('flex w-full min-w-0 flex-col gap-3', className)}>
        <div data-slot="table-container" className="overflow-auto">
          <table
            data-slot="table"
            className={cn('w-full text-sm', hasFixedWidths ? 'table-fixed' : 'table-auto')}
            style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth }}
          >
            <thead data-slot="table-header">
              <tr>
                {expandedRowRender && <th className="w-9 p-3" aria-hidden />}
                {selectable && (
                  <th className="w-10 p-3 text-center">
                    <Checkbox
                      checked={someSelected ? 'indeterminate' : allSelected}
                      onCheckedChange={(v) => {
                        handleSelectAll(!!v);
                      }}
                      aria-label={t('common:actions.selectAll')}
                    />
                  </th>
                )}
                {columns.map((col) => {
                  const colKey = String(col.key);
                  const isSortable = col.sortable && !loading;
                  const currentDirection = sort.key === colKey ? sort.direction : null;
                  const thContent = (
                    <span className="inline-flex items-center gap-1">
                      {col.header}
                      {col.sortable && (
                        <SortIcon direction={isSortable ? currentDirection : null} />
                      )}
                    </span>
                  );
                  return (
                    <th
                      key={colKey}
                      data-slot="table-head"
                      data-sortable={isSortable ? 'true' : undefined}
                      style={col.width ? { width: col.width } : undefined}
                      className={cn(
                        'px-4 py-3 text-left font-medium',
                        col.width ? 'whitespace-normal wrap-break-word' : 'whitespace-nowrap',
                        isSortable && 'cursor-pointer select-none',
                        col.className,
                      )}
                      onClick={
                        isSortable
                          ? () => {
                              handleSort(colKey);
                            }
                          : undefined
                      }
                    >
                      {isSortable ? (
                        <Tooltip>
                          <TooltipTrigger asChild>{thContent}</TooltipTrigger>
                          <TooltipContent side="top">
                            {sortTooltipLabel(currentDirection, t)}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        thContent
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody data-slot="table-body">
              {loading ? (
                Array.from({ length: 3 }).map((_, rowIdx) => (
                  <tr key={rowIdx} data-slot="table-row">
                    {expandedRowRender && (
                      <td data-slot="table-cell" className="w-9 p-3 text-center">
                        <Skeleton className="mx-auto size-4 rounded" />
                      </td>
                    )}
                    {selectable && (
                      <td data-slot="table-cell" className="w-10 p-3 text-center">
                        <Skeleton className="mx-auto size-4 rounded" />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td
                        key={String(col.key)}
                        data-slot="table-cell"
                        style={col.width ? { width: col.width } : undefined}
                        className="px-4 py-3"
                      >
                        <div className="skeleton-shimmer h-4 w-full rounded-md" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : pagedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + (selectable ? 1 : 0) + (expandedRowRender ? 1 : 0)}
                    className="px-4 py-8 text-center text-(--color-text-muted)"
                  >
                    {emptyText ?? t('common:messages.noRecords')}
                  </td>
                </tr>
              ) : (
                pagedData.map((row) => {
                  const key = keyExtractor(row);
                  const isSelected = selectedKeys.has(key);
                  const isExpanded = expandedRowRender ? expandedKeys.has(key) : false;
                  return (
                    <React.Fragment key={key}>
                      <tr
                        data-slot="table-row"
                        data-selected={isSelected ? 'true' : undefined}
                        className="transition-colors"
                      >
                        {expandedRowRender && (
                          <td className="w-9 p-3 text-center">
                            <button
                              type="button"
                              aria-expanded={isExpanded}
                              aria-label={
                                isExpanded ? t('table.collapseRow') : t('table.expandRow')
                              }
                              onClick={() => {
                                toggleExpanded(key);
                              }}
                              className="inline-flex size-6 items-center justify-center rounded-md text-(--color-text-muted) transition-colors hover:bg-(--color-surface-muted) hover:text-(--color-text-primary)"
                            >
                              <ChevronRight
                                className={cn(
                                  'size-4 transition-transform',
                                  isExpanded && 'rotate-90',
                                )}
                              />
                            </button>
                          </td>
                        )}
                        {selectable && (
                          <td className="w-10 p-3 text-center">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(v) => {
                                handleRowSelect(row, !!v);
                              }}
                              aria-label={t('table.selectRow')}
                            />
                          </td>
                        )}
                        {columns.map((col) => {
                          const rawValue = getValue(row, String(col.key));
                          const cellContent = col.render
                            ? col.render(rawValue, row)
                            : String(rawValue ?? '');
                          return (
                            <td
                              key={String(col.key)}
                              data-slot="table-cell"
                              style={col.width ? { width: col.width } : undefined}
                              className={cn('px-4 py-3', col.className)}
                            >
                              {col.width ? (
                                <div className="line-clamp-2 wrap-break-word">{cellContent}</div>
                              ) : (
                                cellContent
                              )}
                            </td>
                          );
                        })}
                      </tr>
                      {expandedRowRender && isExpanded && (
                        <tr data-slot="table-row" className="bg-(--color-surface-muted)">
                          <td
                            colSpan={columns.length + (selectable ? 1 : 0) + 1}
                            className="px-4 py-4"
                          >
                            {expandedRowRender(row)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && serverPagination && (
          <Pagination
            totalItems={serverPagination.totalItems}
            currentPage={serverPagination.currentPage}
            pageSize={serverPagination.pageSize}
            onPageChange={serverPagination.onPageChange}
            onPageSizeChange={serverPagination.onPageSizeChange}
            pageSizeOptions={serverPagination.pageSizeOptions ?? pageSizeOptions}
          />
        )}
        {!loading && !serverPagination && !hidePagination && (
          <Pagination
            totalItems={sortedData.length}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={pageSizeOptions ? setPageSize : undefined}
            pageSizeOptions={pageSizeOptions}
          />
        )}
      </div>
    </TooltipProvider>
  );
}
